import {
  Alert,
  Box,
  Button,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isNil } from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  type PrefillInspectionQuery,
  usePrefillInspectionLazyQuery,
} from "../../generated/graphql";
import { type SelectOption } from "../doc-scanning/schema-testing/types";
import CompanySelect from "../doc-scanning/company-select";
import PrefillResult from "./prefill-result";

const styles = {
  results: { mt: 3 },
} satisfies Record<string, SxProps<Theme>>;

type PrefillInspectionEntity = Extract<
  PrefillInspectionQuery["prefillInspection"],
  { __typename?: "PrefillInspectionEntity" }
>;

/**
 * A settled inspection response together with the company + sender it was
 * fetched for. Held in state so a candidate probe (which re-fetches) leaves the
 * previous result on screen — disabled, not blank — while the new one loads,
 * and so a mid-flight response for a company the user has since changed away
 * from is never shown.
 */
type DisplayedInspection = {
  companyUuid: string;
  senderAddress: string;
  result: PrefillInspectionEntity | null;
  queryError: string | null;
  errorMessage: string | null;
};

/**
 * Internal prefill inspector: pick a company (and optionally an ingesting
 * sender address) to see the zero-LLM billing-party prefill verdicts and the
 * bill-to candidate set the V3 apply path would compute. Selecting a candidate
 * probes its conditional service default. Every field is a replica read behind
 * the `prefillInspection` internal-tools query; nothing here writes.
 */
const IngestionInspector = () => {
  const [companyUuid, setCompanyUuid] = useState<string | null>(null);
  const [senderAddress, setSenderAddress] = useState("");
  const [billingPartyContactUuid, setBillingPartyContactUuid] = useState<
    string | null
  >(null);
  // The company + sender the currently held response was fetched for. Editing
  // either without pressing Inspect again makes that response stale, so the UI
  // gates rendering on this matching the live selection (a candidate probe
  // re-fetches with the same company + sender, so it stays matched).
  const [inspectedInputs, setInspectedInputs] = useState<{
    companyUuid: string;
    senderAddress: string;
  } | null>(null);
  const [displayed, setDisplayed] = useState<DisplayedInspection | null>(null);

  // Lazy + no-cache so each "Inspect" is a fresh replica read, and errorPolicy
  // "all" so a resolved `QueryErrorOutput` (bad uuid) or a transport error both
  // surface instead of silently collapsing to empty.
  const [inspect, { data, loading, error }] = usePrefillInspectionLazyQuery({
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  const runInspection = useCallback(
    (nextBillingPartyContactUuid: string | null) => {
      if (isNil(companyUuid)) {
        return;
      }
      const trimmedSender = senderAddress.trim();
      setInspectedInputs({ companyUuid, senderAddress: trimmedSender });
      void inspect({
        variables: {
          companyUuid,
          senderAddress: trimmedSender === "" ? null : trimmedSender,
          billingPartyContactUuid: nextBillingPartyContactUuid,
        },
      });
    },
    [companyUuid, senderAddress, inspect],
  );

  const handleCompanyChange = useCallback(
    (option: SelectOption | undefined | null) => {
      setCompanyUuid(option?.value ?? null);
      setBillingPartyContactUuid(null);
    },
    [],
  );

  const handleInspect = useCallback(() => {
    setBillingPartyContactUuid(null);
    runInspection(null);
  }, [runInspection]);

  const handleSelectCandidate = useCallback(
    (uuid: string) => {
      setBillingPartyContactUuid(uuid);
      runInspection(uuid);
    },
    [runInspection],
  );

  // Capture each response only once it has settled, tagged with the inputs it
  // was fetched for. Rendering reads from this (never the in-flight `data`), so
  // a response for a since-abandoned company is never shown.
  useEffect(() => {
    if (loading || inspectedInputs === null) {
      return;
    }
    const inspection = data?.prefillInspection;
    setDisplayed({
      companyUuid: inspectedInputs.companyUuid,
      senderAddress: inspectedInputs.senderAddress,
      result:
        inspection?.__typename === "PrefillInspectionEntity"
          ? inspection
          : null,
      queryError:
        inspection?.__typename === "QueryErrorOutput"
          ? inspection.message
          : null,
      errorMessage: error?.message ?? null,
    });
  }, [loading, data, error, inspectedInputs]);

  // Show the settled response only while it still describes the live selection;
  // editing the company or sender without pressing Inspect again hides it.
  const displayMatches =
    displayed !== null &&
    displayed.companyUuid === companyUuid &&
    displayed.senderAddress === senderAddress.trim();
  const result = displayMatches ? displayed.result : null;
  const queryError = displayMatches ? displayed.queryError : null;
  const transportError = displayMatches ? displayed.errorMessage : null;

  return (
    <Box>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={5}>
          <CompanySelect
            currentCompanyUuid={companyUuid}
            onChange={handleCompanyChange}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Sender address (optional)"
            placeholder="ops@shipper.com"
            size="small"
            value={senderAddress}
            onChange={(e) => {
              setSenderAddress(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            fullWidth
            disabled={isNil(companyUuid) || loading}
            variant="contained"
            onClick={handleInspect}
          >
            Inspect
          </Button>
        </Grid>
      </Grid>

      <Stack spacing={2} sx={styles.results}>
        {loading && <LinearProgress />}
        {!isNil(transportError) && (
          <Alert severity="error">
            Failed to run inspection: {transportError}
          </Alert>
        )}
        {!isNil(queryError) && <Alert severity="warning">{queryError}</Alert>}
        {!loading &&
          result === null &&
          isNil(queryError) &&
          isNil(transportError) && (
            <Typography color="text.secondary">
              Select a company and press Inspect to see its prefill verdicts.
            </Typography>
          )}
        {result !== null && (
          <PrefillResult
            loading={loading}
            result={result}
            selectedBillingPartyContactUuid={billingPartyContactUuid}
            onSelectCandidate={handleSelectCandidate}
          />
        )}
      </Stack>
    </Box>
  );
};

export default IngestionInspector;
